import {
  ArrowDownTrayIcon,
  DocumentPlusIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { DataGrid, GridColumn, GridSort } from 'components/DataGrid'
import { HoverTip } from 'components/HoverTip'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  cancelTermChange,
  downloadRateChangeNotice,
  emailRateChangeNotice,
  generateRateChangeNotice,
  getTermChanges,
} from 'services'
import { Button } from 'stories/components'
import { confirm, formatDate } from 'utils'

import { ScheduleChangeModal } from './ScheduleChangeModal'
import { type TermChangeRow, type TermChangeSettings, formatTermValue } from './types'

const STATUS_STYLE: Record<string, string> = {
  scheduled: 'bg-amber-50 text-amber-800',
  applied: 'bg-green-50 text-green-800',
  cancelled: 'bg-slate-100 text-slate-500 line-through',
}

/**
 * Loan > Servicing > Rate & Payment Changes: every dated change to the loan's interest rate, P&I
 * and impound, past and scheduled, and where each came from (entered, a step-rate schedule, an ARM
 * adjustment, an escrow analysis, a payment recalculated after a rate change).
 *
 * Every calculation reads this schedule by date - statements, amount due, ACH drafts, payoff and
 * reinstatement quotes, late charges, credit reporting, the investor split - so a change entered
 * here is honored from the moment it is saved, and the loan's Servicing Information catches up on
 * the day it takes effect.
 */
export default function RateChanges() {
  const { hasPermission } = usePermissions()
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<TermChangeRow[]>([])
  const [settings, setSettings] = useState<TermChangeSettings | null>(null)
  const [sort, setSort] = useState<GridSort | null>(null)
  const [modal, setModal] = useState<{ editing: TermChangeRow | null } | null>(null)
  const [showCancelled, setShowCancelled] = useState(false)
  const [noticeBusy, setNoticeBusy] = useState<number | null>(null)

  const allowedFields = useMemo(() => {
    const fields: string[] = []
    if (hasPermission('Loan.Servicing.ServicingInformation.Terms.Edit')) fields.push('rate')
    if (hasPermission('Loan.Servicing.ServicingInformation.Payments.Edit')) fields.push('payment', 'escrow')
    return fields
  }, [hasPermission])

  const load = () => {
    setLoading(true)
    getTermChanges()
      .then((res: any) => {
        setRows(res?.changes || [])
        setSettings(res?.settings || null)
      })
      .catch((err: any) => toast(err?.message || 'Could not load the changes', { type: 'error' }))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    load()
  }, [])

  const onCancel = async (row: TermChangeRow) => {
    const recast =
      row.field === 'rate' ? ' A payment recalculated from it that has not taken effect is cancelled too.' : ''
    if (
      !(await confirm(
        `Cancel the ${row.fieldLabel} change to ${formatTermValue(row.field, row.value)} on ${formatDate(
          row.effectiveDate,
        )}?${recast}`,
      ))
    )
      return
    try {
      await cancelTermChange(row.id)
      load()
    } catch (err: any) {
      toast(err?.message || 'Could not cancel the change', { type: 'error' })
    }
  }

  /**
   * Produce the borrower's notice for a change (a fresh one each time). Changes dated the same day
   * share one notice: a rate and its new P&I, or a P&I and escrow change together.
   */
  const onGenerateNotice = async (row: TermChangeRow) => {
    setNoticeBusy(row.id)
    try {
      await generateRateChangeNotice(row.id)
      toast("Notice filed to the loan's documents.", { type: 'success' })
      load()
    } catch (err: any) {
      toast(err?.message || 'Could not produce the notice', { type: 'error' })
    } finally {
      setNoticeBusy(null)
    }
  }

  const onDownloadNotice = async (row: TermChangeRow) => {
    setNoticeBusy(row.id)
    try {
      const blob: Blob = await downloadRateChangeNotice(row.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = row.noticeFiles?.[row.noticeFiles.length - 1]?.fileName || `change-notice-${row.id}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast('Could not download the notice.', { type: 'error' })
    } finally {
      setNoticeBusy(null)
    }
  }

  const onEmailNotice = async (row: TermChangeRow) => {
    const latest = row.noticeFiles?.[row.noticeFiles.length - 1]
    const again = latest?.emailedTo ? ` It was already sent to ${latest.emailedTo}.` : ''
    if (!(await confirm(`Email the notice for ${formatDate(row.effectiveDate)} to the borrower?${again}`)))
      return
    setNoticeBusy(row.id)
    try {
      const res: any = await emailRateChangeNotice(row.id)
      toast(res?.message || (res?.success ? 'Notice emailed.' : 'The notice was not emailed.'), {
        type: res?.success ? 'success' : 'error',
      })
      if (res?.success) load()
    } finally {
      setNoticeBusy(null)
    }
  }

  const columns = useMemo<GridColumn<TermChangeRow>[]>(
    () => [
      {
        key: 'effectiveDate',
        label: 'Effective',
        sortable: true,
        hideable: false,
        defaultWidth: 110,
        render: (r) => formatDate(r.effectiveDate),
      },
      { key: 'fieldLabel', label: 'Change', sortable: true, defaultWidth: 190 },
      {
        key: 'value',
        label: 'New Value',
        align: 'right',
        defaultWidth: 110,
        render: (r) => <span className="font-medium text-gray-900">{formatTermValue(r.field, r.value)}</span>,
      },
      {
        key: 'previousValue',
        label: 'Was',
        align: 'right',
        defaultWidth: 110,
        render: (r) => <span className="text-gray-500">{formatTermValue(r.field, r.previousValue)}</span>,
      },
      { key: 'sourceLabel', label: 'Source', sortable: true, defaultWidth: 170 },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        defaultWidth: 110,
        render: (r) => (
          <span className={`text-xs font-medium rounded px-2 py-0.5 capitalize ${STATUS_STYLE[r.status] || ''}`}>
            {r.status}
          </span>
        ),
      },
      {
        key: 'note',
        label: 'Note',
        defaultWidth: 320,
        render: (r) => <span className="text-xs text-gray-600">{r.note}</span>,
      },
      {
        key: 'noticeFiles',
        label: 'Borrower Notice',
        defaultWidth: 220,
        render: (r) => {
          if (r.status === 'cancelled') return null
          const canNotice = allowedFields.includes(r.field)
          const latest = r.noticeFiles?.[r.noticeFiles.length - 1]
          const busy = noticeBusy === r.id
          return (
            <div className={`flex items-center gap-2.5 text-slate-500 ${busy ? 'opacity-40' : ''}`}>
              <span className="text-xs">
                {latest ? (
                  latest.emailedTo ? (
                    <span className="text-green-700">Emailed {formatDate(latest.emailedAt || '')}</span>
                  ) : (
                    <span>Filed {formatDate(latest.createdAt)}</span>
                  )
                ) : (
                  <span className="text-gray-400">None</span>
                )}
              </span>
              {latest && (
                <HoverTip message="Download the notice">
                  <button
                    aria-label="Download notice"
                    className="hover:text-shade-blue"
                    disabled={busy}
                    onClick={() => onDownloadNotice(r)}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                </HoverTip>
              )}
              {canNotice && (
                <HoverTip message={latest ? 'Produce a fresh notice' : 'Produce the notice'}>
                  <button
                    aria-label="Produce notice"
                    className="hover:text-shade-blue"
                    disabled={busy}
                    onClick={() => onGenerateNotice(r)}
                  >
                    <DocumentPlusIcon className="h-4 w-4" />
                  </button>
                </HoverTip>
              )}
              {canNotice && (
                <HoverTip message="Email the notice to the borrower">
                  <button
                    aria-label="Email notice"
                    className="hover:text-shade-blue"
                    disabled={busy}
                    onClick={() => onEmailNotice(r)}
                  >
                    <EnvelopeIcon className="h-4 w-4" />
                  </button>
                </HoverTip>
              )}
            </div>
          )
        },
      },
      { key: 'createdBy', label: 'Entered By', sortable: true, defaultWidth: 170 },
      {
        key: 'actions',
        label: 'Actions',
        hideable: false,
        reorderable: false,
        defaultWidth: 90,
        render: (r) =>
          r.status === 'scheduled' && allowedFields.includes(r.field) ? (
            <div className="flex items-center gap-3 text-slate-500">
              <HoverTip message="Edit this scheduled change">
                <button
                  aria-label="Edit change"
                  className="hover:text-shade-blue"
                  onClick={() => setModal({ editing: r })}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
              </HoverTip>
              <HoverTip message="Cancel this scheduled change">
                <button aria-label="Cancel change" className="hover:text-red-700" onClick={() => onCancel(r)}>
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </HoverTip>
            </div>
          ) : null,
      },
    ],
    [allowedFields, noticeBusy],
  )

  const visibleRows = useMemo(() => {
    const kept = showCancelled ? rows : rows.filter((r) => r.status !== 'cancelled')
    if (!sort) return kept
    const { key, dir } = sort
    return [...kept].sort((a, b) => {
      const cmp = String((a as any)[key] ?? '').localeCompare(String((b as any)[key] ?? ''), undefined, {
        numeric: true,
      })
      return dir === 'asc' ? cmp : -cmp
    })
  }, [rows, sort, showCancelled])

  const upcoming = rows.filter((r) => r.status === 'scheduled').length
  const cancelled = rows.filter((r) => r.status === 'cancelled').length

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div>
          <h2 className="text-2xl font-bold">Rate &amp; Payment Changes</h2>
          <p className="text-sm text-gray-500">
            {rows.length ? `${upcoming} scheduled, ${rows.length - upcoming - cancelled} applied` : 'No changes yet'}
          </p>
        </div>
        {allowedFields.length > 0 && (
          <Button color="blue" className="mb-0" onClick={() => setModal({ editing: null })}>
            Schedule a Change
          </Button>
        )}
      </div>

      {settings && (
        <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4">
          <div>
            <span className="text-slate-500">Rate change mid-period:</span> {settings.rateChangeAccrualText}
            <span className="text-slate-400">
              {' '}
              ({settings.rateChangeAccrualFromLoan ? 'this loan' : 'company default'})
            </span>
          </div>
          <div>
            <span className="text-slate-500">When the rate changes on an amortizing loan:</span>{' '}
            {settings.rateChangeRecastText}
            <span className="text-slate-400">
              {' '}
              ({settings.rateChangeRecastFromLoan ? 'this loan' : 'company default'})
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Set per loan on Servicing Information, or for the company in Admin Tools &gt; Configuration &gt; Rules.
          </div>
        </div>
      )}

      {cancelled > 0 && (
        <label className="text-xs text-gray-500 flex items-center gap-1.5 mb-2">
          <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
          Show {cancelled} cancelled
        </label>
      )}

      <DataGrid<TermChangeRow>
        gridKey="loan-servicing-term-changes"
        columns={columns}
        rows={visibleRows}
        rowKey={(r) => r.id}
        sort={sort}
        onSortChange={setSort}
        loading={loading}
        emptyText="No rate, payment or escrow changes on this loan. Use “Schedule a Change” to add one, or several for a step-rate loan."
      />

      {modal && (
        <ScheduleChangeModal
          editing={modal.editing}
          allowedFields={modal.editing ? [modal.editing.field] : allowedFields}
          existing={rows}
          onClose={(changed) => {
            setModal(null)
            if (changed) load()
          }}
        />
      )}
    </div>
  )
}
